import { useMemo, useState } from "react";
import type { Dataset } from "../lib/types";
import { drawSentimentTimeline, type SentimentBucket } from "../lib/charts";
import Card from "../components/Card";
import Chart from "../components/Chart";
import Sparkline from "../components/Sparkline";
import { showToast } from "../components/Toast";
import { exportCsv } from "../lib/export";

interface Props { data: Dataset }

function fmtPct(v: number, sign = true): string {
  const s = sign && v > 0 ? "+" : "";
  return `${s}${(v * 100).toFixed(1)}%`;
}
function fmtMoneyB(v: number): string {
  return v >= 1 ? `$${v.toFixed(1)}B` : `$${(v * 1000).toFixed(0)}M`;
}

const SENTIMENT_INFO = {
  title: "News sentiment timeline",
  text:
    "Volume of sector-relevant news per week (bars: green = positive, gray = neutral, red = negative) overlaid with the volume-weighted net-sentiment line above. Net > +0.15 favorable; < −0.15 adverse.",
  foot: "Source: GDELT v2 + Bing News API · scoring: FinBERT-construction",
};
const COMMODITY_INFO = {
  title: "Commodity spike monitor",
  text:
    "Z-score on the latest BLS PPI / EIA print against the trailing-12 baseline. Spike (z > 2σ) → triggers pricing-clause review on fixed-price exposure. Sparklines show 24-month trend.",
  foot: "Source: BLS PPI data API · EIA · 24-month trailing window · z = (latest − μ) / σ",
};
const MACRO_INFO = {
  title: "Sector macro indicators",
  text:
    "Construction-relevant macro signals with their typical lead time. ABI is the highest-conviction leading indicator (9-12m). Dodge Starts is concurrent. ENR CCI and PPI Construction are lagging cost trackers. SLOOS tells us about customer-side credit conditions.",
  foot: "Sources: AIA · Dodge · ENR · BLS · FRED · SLOOS",
};
const SEGMENTS_INFO = {
  title: "Customer-industry segment outlook",
  text:
    "5 customer-industry segments mapped to project archetype, with 12-month capex outlook from public Q1'26 disclosures + SEC capex guidance + FMI Outlook. Our backlog exposure column lets you compare where the market is going to where we're positioned.",
  foot: "Production swap: live capex tracker via 10-K guidance + analyst consensus",
};
const NEWS_INFO = {
  title: "Top stories — last 90 days",
  text:
    "Highest-relevance items from the news feed. Each item has a sentiment score (−1 to +1) and an explicit operational implication. Click any item to drill into the full story (placeholder).",
};

function flagSeverity(flag: string): "good" | "warn" | "bad" | "neutral" {
  if (flag === "SPIKE") return "bad";
  if (flag === "WATCH") return "warn";
  if (flag === "OK") return "good";
  return "neutral";
}

function bucketSentiment(items: Dataset["industry_news"]["items"], asOf: string): SentimentBucket[] {
  // Bucket into ~13 weekly bins ending at asOf.
  const end = new Date(asOf).getTime();
  const weekMs = 7 * 86_400_000;
  const buckets: SentimentBucket[] = [];
  for (let i = 12; i >= 0; i--) {
    const wkStart = end - (i + 1) * weekMs;
    const wkEnd = end - i * weekMs;
    const inBucket = items.filter((it) => {
      const t = new Date(it.date).getTime();
      return t >= wkStart && t < wkEnd;
    });
    const positive = inBucket.filter((it) => it.sentiment > 0.15).length;
    const negative = inBucket.filter((it) => it.sentiment < -0.15).length;
    const neutral = inBucket.length - positive - negative;
    const sumW = inBucket.reduce((s, it) => s + it.sentiment, 0);
    const netSentiment = inBucket.length > 0 ? sumW / inBucket.length : 0;
    const lblDate = new Date(wkEnd - 1);
    const wkOfYear = Math.ceil(((lblDate.getTime() - new Date(lblDate.getFullYear(), 0, 1).getTime()) / 86_400_000 + 1) / 7);
    buckets.push({ label: `W${wkOfYear}`, positive, neutral, negative, netSentiment });
  }
  return buckets;
}

export default function Industry({ data }: Props) {
  const [tagFilter, setTagFilter] = useState<string>("ALL");
  const commodities = data.industry_commodities.items;
  const macros = data.industry_macro.items;
  const segments = data.industry_segments.items;
  const news = data.industry_news.items;

  const sentimentBuckets = useMemo(() => bucketSentiment(news, data.meta.asOf), [news, data.meta.asOf]);
  const allTags = useMemo(() => Array.from(new Set(news.flatMap((n) => n.tags))).sort(), [news]);
  const filteredNews = useMemo(
    () =>
      [...news]
        .filter((n) => tagFilter === "ALL" || n.tags.includes(tagFilter))
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 12),
    [news, tagFilter]
  );

  const exportCommodities = () => {
    exportCsv(
      commodities,
      [
        { header: "Code", value: (c) => c.code },
        { header: "Name", value: (c) => c.name },
        { header: "Series", value: (c) => c.series },
        { header: "Latest", value: (c) => c.latest },
        { header: "MoM %", value: (c) => (c.monthDeltaPct).toFixed(2) },
        { header: "YoY %", value: (c) => (c.yearDeltaPct).toFixed(2) },
        { header: "Z-score", value: (c) => c.z.toFixed(2) },
        { header: "Flag", value: (c) => c.flag },
        { header: "Exposure archetype", value: (c) => c.exposureArchetype },
        { header: "Implication", value: (c) => c.implication },
      ],
      `industry-commodities-${data.industry_commodities.asOf}`
    );
    showToast("Commodity feed exported (CSV)", "success");
  };

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">
            Industry <span className="page-pill">SECTORAL</span>
          </h1>
          <p className="page-sub">
            External signals feeding the forecast as covariates: raw-material commodity spikes, sector macro
            indicators, customer-industry segment outlook, and weighted news sentiment. Polls live
            BLS · AIA · Dodge · GDELT feeds and re-derives the indicators on the platform's auto-refresh cadence.
          </p>
        </div>
        <div className="page-controls">
          <span className="filter-pill">
            <span className="filter-label">As-of</span>
            <span className="filter-value">{data.industry_commodities.asOf}</span>
          </span>
        </div>
      </div>

      {/* Macro indicator strip — like a hero KPI band */}
      <Card eyebrow="K-141 · K-142 · K-143 · K-181 · K-190 · K-194" title="Sector macro indicators" info={MACRO_INFO}>
        <div className="industry-macro-grid">
          {macros.map((m) => {
            const sparkDir = m.trend === "down" ? "bad" : m.trend === "warn" ? "warn" : "good";
            return (
              <div key={m.id} className="industry-macro-tile">
                <div className="industry-macro-head">
                  <span className="industry-macro-code">{m.code}</span>
                  <span className="industry-macro-lead">lead {m.lead}</span>
                </div>
                <div className="industry-macro-name">{m.name}</div>
                <div className="industry-macro-row">
                  <div className="industry-macro-val">{m.latest.toLocaleString("en-US", { maximumFractionDigits: m.latest >= 1000 ? 0 : 2 })}</div>
                  <div className={`industry-macro-trend ${m.trend}`}>{m.monthChangeLabel}</div>
                </div>
                <Sparkline values={m.spark} direction={sparkDir as any} className="industry-macro-spark" viewBox="0 0 200 24" />
                <div className="industry-macro-imp">{m.implication}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Commodity grid + sentiment timeline */}
      <div className="row-2">
        <Card
          span2
          eyebrow="K-103 — K-110"
          title={`Commodity spike monitor (${commodities.length})`}
          info={COMMODITY_INFO}
          meta={
            <button className="card-export" onClick={exportCommodities}>
              ⬇ Export CSV
            </button>
          }
          foot={
            <div className="callout small">
              <strong>{commodities.filter((c) => c.flag === "SPIKE").length}</strong> at SPIKE (z &gt; 2σ) ·
              <strong> {commodities.filter((c) => c.flag === "WATCH").length}</strong> at WATCH ·
              <strong> {commodities.filter((c) => c.flag === "OK").length}</strong> within tolerance
            </div>
          }
        >
          <div className="commodity-grid">
            {commodities.map((c) => {
              const flagCls = c.flag === "SPIKE" ? "spike-bad" : c.flag === "WATCH" ? "spike-warn" : "";
              const flagBg =
                c.flag === "SPIKE" ? "rgba(220,38,38,0.18)" :
                c.flag === "WATCH" ? "rgba(217,119,6,0.18)" :
                "rgba(5,150,105,0.14)";
              const flagFg =
                c.flag === "SPIKE" ? "var(--critical)" :
                c.flag === "WATCH" ? "var(--warning)" :
                "var(--good)";
              const dir = flagSeverity(c.flag);
              return (
                <div key={c.id} className={`material-tile ${flagCls}`}>
                  <div className="material-tile-head">
                    <div className="material-tile-name">{c.name}</div>
                    <span className="material-tile-flag" style={{ background: flagBg, color: flagFg }}>
                      {c.flag}
                    </span>
                  </div>
                  <div className="material-tile-row">
                    <div>
                      <span className="material-tile-val">{c.latest.toLocaleString("en-US", { maximumFractionDigits: 1 })}</span>
                      <span style={{ fontSize: 10, color: "var(--t4)", marginLeft: 4 }}>{c.unit.split(" ")[0]}</span>
                    </div>
                    <div className="material-tile-delta" style={{ color: c.monthDeltaPct < 0 ? "var(--good)" : c.monthDeltaPct > 1 ? "var(--critical)" : "var(--warning)" }}>
                      {c.monthDeltaPct >= 0 ? "+" : ""}{c.monthDeltaPct.toFixed(1)}% m/m
                    </div>
                  </div>
                  <Sparkline values={c.spark} direction={dir as any} className="material-tile-spark" viewBox="0 0 200 24" />
                  <div className="material-tile-z">
                    {c.code} · z = {c.z >= 0 ? "+" : ""}{c.z.toFixed(1)}σ · exposure: {c.exposureArchetype}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card eyebrow="GDELT · 13 weeks" title="News sentiment timeline" info={SENTIMENT_INFO} ai
          foot={
            <div className="callout small">
              <span className="callout-tag info">SENTIMENT</span>
              {(() => {
                const last = sentimentBuckets[sentimentBuckets.length - 1];
                if (!last || last.positive + last.neutral + last.negative === 0) return "No items in latest week.";
                const dir = last.netSentiment > 0.15 ? "favorable" : last.netSentiment < -0.15 ? "adverse" : "mixed";
                return `Latest week: ${last.positive + last.neutral + last.negative} items, net sentiment ${last.netSentiment >= 0 ? "+" : ""}${last.netSentiment.toFixed(2)} (${dir}).`;
              })()}
            </div>
          }
        >
          <Chart viewBox="0 0 460 220" draw={(svg) => drawSentimentTimeline(svg, sentimentBuckets)} deps={[sentimentBuckets]} />
        </Card>
      </div>

      {/* Segment outlook + top stories */}
      <div className="row-2">
        <Card
          span2
          eyebrow="Customer-industry capex outlook · 12m"
          title="Segment outlook"
          info={SEGMENTS_INFO}
        >
          <div className="segment-grid">
            {segments.map((s) => {
              const sevColor =
                s.outlookSeverity === "good" ? "var(--good)" :
                s.outlookSeverity === "warn" ? "var(--warning)" :
                s.outlookSeverity === "bad" ? "var(--critical)" :
                "var(--info)";
              return (
                <div key={s.id} className="segment-card" style={{ borderLeftColor: sevColor }}>
                  <div className="segment-head">
                    <div>
                      <div className="segment-name">{s.name}</div>
                      <div className="segment-meta">
                        NAICS {s.naics} · maps to {s.archetype}
                      </div>
                    </div>
                    <span className={`segment-outlook ${s.outlookSeverity}`}>{s.outlook}</span>
                  </div>
                  <div className="segment-stats">
                    <div>
                      <span className="segment-stat-label">12m capex</span>
                      <span className="segment-stat-val">{fmtMoneyB(s.capexNext12mB)}</span>
                      <span className={`segment-stat-delta ${s.capexDeltaYoY >= 0 ? "good" : "bad"}`}>
                        {fmtPct(s.capexDeltaYoY)} YoY
                      </span>
                    </div>
                    <div>
                      <span className="segment-stat-label">Our backlog</span>
                      <span className="segment-stat-val">{fmtMoneyB(s.ourBacklogM / 1000)}</span>
                      <span className="segment-stat-delta">{s.ourActiveProjects} active</span>
                    </div>
                  </div>
                  <div className="segment-drivers">
                    {s.drivers.map((d) => (
                      <span key={d} className="segment-driver">{d}</span>
                    ))}
                  </div>
                  <div className="segment-imp">{s.implication}</div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card
          eyebrow={`${news.length} items · 90 days`}
          title="Top stories"
          info={NEWS_INFO}
          meta={
            <select
              className="filter-pill"
              style={{ padding: "5px 10px", fontSize: 11.5 }}
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              aria-label="Filter by tag"
            >
              <option value="ALL">All tags ({allTags.length})</option>
              {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          }
        >
          <ul className="news-list">
            {filteredNews.map((n) => {
              const sevTone = n.sentiment > 0.15 ? "good" : n.sentiment < -0.15 ? "bad" : "neutral";
              return (
                <li key={n.id} className="news-item">
                  <div className="news-head">
                    <span className={`news-sentiment ${sevTone}`}>
                      {n.sentiment >= 0 ? "+" : ""}{n.sentiment.toFixed(2)}
                    </span>
                    <span className="news-source">{n.source}</span>
                    <span className="news-date">{n.date}</span>
                  </div>
                  <div className="news-headline">{n.headline}</div>
                  <div className="news-tags">
                    {n.tags.slice(0, 4).map((t) => (
                      <span key={t} className="news-tag">{t}</span>
                    ))}
                  </div>
                  <div className="news-imp">{n.implication}</div>
                </li>
              );
            })}
            {filteredNews.length === 0 && (
              <li className="news-item" style={{ textAlign: "center", color: "var(--t4)", fontSize: 12 }}>
                No items match this tag.
              </li>
            )}
          </ul>
        </Card>
      </div>
    </section>
  );
}
